<template>
  <div class="global">
    <div v-if="orderStatusNotifications.length" :class="['order-status-notifications-stack', { fullScreen: stackFullScreen, walletShell: isWalletNotificationsStack }]">
      <OrderStatusNotification v-for="item in orderStatusNotifications" :key="item.id" :infos="item.infos" @dismiss="removeOrderStatusNoticeById(item.id)" />
    </div>
    <ChatServies v-if="chatServiesUrl" :chatUrl="chatServiesUrl" @close="handleCloseChatServies" />
  </div>
</template>

<script setup>
import { defineAsyncComponent, ref, computed, onMounted, watch } from 'vue';
import dayjs from 'dayjs';
import { counterInfoGet, orderCountRC } from '@/utils/counter/data';
import { walletInfoRC } from '@/utils/wallet/data';
import { setEventComparison as setWalletEventComparison, runEvent as runWalletEvent } from '@/utils/wallet/event';
import { setEventComparison as setCounterEventComparison, runEvent as runCounterEvent } from '@/utils/counter/event';
import { getProcessStatic } from '@/api/counter';
import { useRoute } from 'vue-router';

let orderNoticeIdSeq = 0;

const route = useRoute();

/** 内嵌 68（/wallet-embedded）：订单通知一旦在某 WebView 展示过，同源其它实例不再展示。
 * 存 localStorage：最多 48 条 + 1 天 TTL；walletInfoRC(null) 时整表清空（见 wallet/data.js） */
const WALLET_EMBEDDED_SHOWN_ORDER_NOTICE_KEYS = 'walletEmbeddedShownOrderNoticeKeys';
const WALLET_EMBEDDED_SHOWN_ORDER_NOTICE_MAX = 48;
const WALLET_EMBEDDED_SHOWN_NOTICE_TTL_MS = 24 * 60 * 60 * 1000;

const isWalletEmbeddedPath = () => typeof location !== 'undefined' && location.pathname.indexOf('/wallet-embedded') === 0;

/** 内嵌且钱包已初始化：walletInfoRC 在 embedded 下会带 plat=1 */
const shouldDedupeWalletEmbeddedOrderNotice = () => isWalletEmbeddedPath() && !!walletInfoRC();

/** 解析为 { k, t }[] 并剔除过期；兼容旧版 string[] */
const loadWalletEmbeddedShownNoticeEntries = () => {
  try {
    const raw = localStorage.getItem(WALLET_EMBEDDED_SHOWN_ORDER_NOTICE_KEYS);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) return [];
    const now = Date.now();
    if (typeof parsed[0] === 'string') {
      return parsed
        .filter(k => typeof k === 'string' && k !== '')
        .map(k => ({ k, t: now }))
        .filter(e => now - e.t < WALLET_EMBEDDED_SHOWN_NOTICE_TTL_MS);
    }
    return parsed.filter(e => e && typeof e.k === 'string' && typeof e.t === 'number').filter(e => now - e.t < WALLET_EMBEDDED_SHOWN_NOTICE_TTL_MS);
  } catch {
    return [];
  }
};

const persistWalletEmbeddedShownNoticeEntries = entries => {
  const sliced = entries.slice(-WALLET_EMBEDDED_SHOWN_ORDER_NOTICE_MAX);
  localStorage.setItem(WALLET_EMBEDDED_SHOWN_ORDER_NOTICE_KEYS, JSON.stringify(sliced));
};

const readWalletEmbeddedShownNoticeKeySet = () => new Set(loadWalletEmbeddedShownNoticeEntries().map(e => e.k));

const appendWalletEmbeddedShownNoticeKey = key => {
  if (!key || !isWalletEmbeddedPath()) return;
  try {
    let entries = loadWalletEmbeddedShownNoticeEntries();
    const now = Date.now();
    entries = entries.filter(e => e.k !== key);
    entries.push({ k: key, t: now });
    persistWalletEmbeddedShownNoticeEntries(entries);
  } catch {
    //
  }
};

/** 与 WS 订单结束等 payload 对齐，用于跨 WebView 去重「已展示」 */
const buildWalletEmbeddedNoticeDedupeKey = infos => {
  if (!infos || typeof infos !== 'object') return '';
  const ty = infos.type != null ? String(infos.type) : '';
  const orderNo = infos.orderNo != null && String(infos.orderNo) !== '' ? String(infos.orderNo) : '';
  const mchOrderNo = infos.mchOrderNo != null && String(infos.mchOrderNo) !== '' ? String(infos.mchOrderNo) : '';
  const st = infos.sendTime != null && String(infos.sendTime) !== '' ? String(infos.sendTime) : '';
  const orderKey = orderNo || mchOrderNo;
  if (orderKey || st) {
    return `${ty}|${orderKey}|${st}`;
  }
  const title = typeof infos.title === 'string' ? infos.title.slice(0, 40) : '';
  const content = typeof infos.content === 'string' ? infos.content.slice(0, 48) : '';
  return `${ty}|${title}|${content}`;
};

/** 钱包 / 内嵌壳：顶部订单通知走 risk 横幅样式（带订单号、时间） */
const isWalletShellPath = p => typeof p === 'string' && (p.startsWith('/wallet') || p.startsWith('/wallet-embedded'));

/** 订单通知堆叠：钱包 / 内嵌壳顶部距与柜台不同 */
const isWalletNotificationsStack = computed(() => isWalletShellPath(route.path));

/** 订单结束横幅：订单号、展示时间（收银台 / 钱包共用） */
const walletOrderStatusPayload = infos => {
  const st = infos.sendTime;
  const displayTime = st != null && String(st) !== '' && Number.isFinite(Number(st)) ? dayjs(Number(st)).format('YYYY-MM-DD HH:mm') : dayjs().format('YYYY-MM-DD HH:mm');
  return {
    walletOrderDetail: true,
    orderNo: infos.orderNo,
    mchOrderNo: infos.mchOrderNo,
    displayTime,
  };
};

// 统一事件触发/监听
const runEvent = (name, params) => {
  runWalletEvent(name, params);
  runCounterEvent(name, params);
};

const setEventComparison = (name, fn) => {
  setWalletEventComparison(name, fn);
  setCounterEventComparison(name, fn);
};

// 是否为收银台
const isCounter = window.location.pathname.includes('/counter');

// 异步引用组件
const OrderStatusNotification = defineAsyncComponent(() => import('@/components/order-status-notification.vue'));
const ChatServies = defineAsyncComponent(() => import('@/components/chat-servies.vue'));

/** 堆叠容器是否与原先单条一致：全屏收银台顶部留白（挂载后再读，避免早于 counterInfoInit） */
const stackFullScreen = ref(!!counterInfoGet().isFullScreen || (typeof document !== 'undefined' && document.body?.classList.contains('fullScreen')));
const chatServiesUrl = ref(''); // 客服url
/** 同时展示的订单状态横幅（最多 ORDER_NOTICE_VISIBLE_MAX 条） */
const orderStatusNotifications = ref([]);
/** 超出上限时排队，有展示位再按 FIFO 补位 */
const orderStatusNotificationQueue = ref([]);
/** @type {Map<number, ReturnType<typeof setTimeout>>} */
const noticeDismissTimers = new Map();
const pollingOrderInfoTimer = ref(null); // 轮询订单信息定时器

/** 通知堆叠：最多同时展示条数、队列补位间隔、自动消失时长 */
const ORDER_NOTICE_VISIBLE_MAX = 5;
const ORDER_NOTICE_PROMOTE_DELAY_MS = 450;
const ORDER_NOTICE_AUTO_DISMISS_MS = 3000;
/** @type {ReturnType<typeof setTimeout> | null} */
let orderNoticePromoteTimer = null;

const scheduleNoticeAutoDismiss = id => {
  noticeDismissTimers.set(
    id,
    setTimeout(() => removeOrderStatusNoticeById(id), ORDER_NOTICE_AUTO_DISMISS_MS)
  );
};

/** 从队列补一条到展示位；返回是否还可继续补 */
const promoteOneFromQueue = () => {
  const vis = orderStatusNotifications.value;
  const que = orderStatusNotificationQueue.value;
  if (vis.length >= ORDER_NOTICE_VISIBLE_MAX || !que.length) return false;
  const [next, ...rest] = que;
  orderStatusNotificationQueue.value = rest;
  orderStatusNotifications.value = [...vis, next];
  if (!next.manualDismissOnly) scheduleNoticeAutoDismiss(next.id);
  return orderStatusNotifications.value.length < ORDER_NOTICE_VISIBLE_MAX && rest.length > 0;
};

/** 延迟补位；多条队列时逐条间隔 ORDER_NOTICE_PROMOTE_DELAY_MS */
const schedulePromoteFromQueue = () => {
  if (orderNoticePromoteTimer) {
    clearTimeout(orderNoticePromoteTimer);
    orderNoticePromoteTimer = null;
  }
  if (!orderStatusNotificationQueue.value.length || orderStatusNotifications.value.length >= ORDER_NOTICE_VISIBLE_MAX) return;
  orderNoticePromoteTimer = setTimeout(() => {
    orderNoticePromoteTimer = null;
    if (!orderStatusNotificationQueue.value.length || orderStatusNotifications.value.length >= ORDER_NOTICE_VISIBLE_MAX) return;
    if (promoteOneFromQueue()) schedulePromoteFromQueue();
  }, ORDER_NOTICE_PROMOTE_DELAY_MS);
};

/**
 * 初始化
 */
onMounted(() => {
  stackFullScreen.value = !!counterInfoGet().isFullScreen || document.body.classList.contains('fullScreen');

  // 轮询订单信息
  setTimeout(() => {
    if (isCounter) {
      // 轮询订单信息
      handlePollingOrderInfo();
    }
  }, 500);

  // 内嵌多 WebView：返回买卖页等 route 可能不变，用 visibility 同步已在他处展示过的通知
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      pruneOrderNotificationsAlreadyShownElsewhere();
    }
  });

  // 监听postmessage
  window.addEventListener(
    'message',
    function (event) {
      if (event && event.data && event.data.type) {
        switch (event.data.type) {
          case 'onClose': {
            chatServiesUrl.value = '';
            break;
          }
          case 'counterRefreshBalance': {
            runEvent('balanceRefresh');
            break;
          }
          case 'paste': {
            runEvent('paste', event.data.value);
            break;
          }
          default: {
            break;
          }
        }
      }
    },
    true
  );

  // 监听订单状态通知
  setEventComparison('orderStatusNotification', data => {
    // 68C_WALLET 等到ws推送状态后再关闭loading
    if (data && data.payCode === '68C_WALLET') {
      window.$loading(false);
    }
    // 资金明细详情页等：按订单号匹配后刷新（不依赖通知是否因去重未展示）
    runWalletEvent('walletOrderEndNotification', data);
    // 显示订单状态通知
    handleNoticeShow(data);
  });
});

// 设置监听客服打开
setEventComparison('setChatServiesUrl', url => {
  // wallet钱包客服url
  if (typeof url === 'object' && url.from === 'wallet') {
    chatServiesUrl.value = url.url;
    return;
  }
  // counter收银台客服url
  const localData = localStorage.getItem('counterInfo');
  const localDataObj = localData ? JSON.parse(localData) : {};
  chatServiesUrl.value = Number(route.query.plat || localDataObj?.plat || '') === 2 ? url : url + '&isapp=1';
});

/**
 * counter处理静态变化 轮询订单状态的补充 ws监听
 * 当ws 推送订单有变化了，重新调用一次接口
 */
setEventComparison('CounterProcessStaticChange', () => {
  handlePollingOrderInfo(true);
});

/**
 * 关闭客服
 */
const handleCloseChatServies = () => {
  chatServiesUrl.value = '';

  // 通知客服关闭
  window.parent.postMessage(
    {
      type: 'chatServies',
      msg: '客服关闭',
      value: 'close',
    },
    '*'
  );
};

const removeOrderStatusNoticeById = id => {
  const vis = orderStatusNotifications.value;
  if (vis.some(n => n.id === id)) {
    orderStatusNotifications.value = vis.filter(n => n.id !== id);
  } else {
    orderStatusNotificationQueue.value = orderStatusNotificationQueue.value.filter(n => n.id !== id);
  }
  const t = noticeDismissTimers.get(id);
  if (t != null) {
    clearTimeout(t);
    noticeDismissTimers.delete(id);
  }
  schedulePromoteFromQueue();
};

const pruneOrderNotificationsAlreadyShownElsewhere = () => {
  if (!shouldDedupeWalletEmbeddedOrderNotice()) return;
  const shown = readWalletEmbeddedShownNoticeKeySet();
  if (!shown.size) return;
  const keep = n => !n.dedupeKey || !shown.has(n.dedupeKey);
  const prevV = orderStatusNotifications.value;
  const prevQ = orderStatusNotificationQueue.value;
  const nextV = prevV.filter(keep);
  const nextQ = prevQ.filter(keep);
  if (nextV.length === prevV.length && nextQ.length === prevQ.length) return;

  const keepIds = new Set([...nextV, ...nextQ].map(n => n.id));
  for (const n of [...prevV, ...prevQ]) {
    if (!keepIds.has(n.id)) {
      const td = noticeDismissTimers.get(n.id);
      if (td != null) {
        clearTimeout(td);
        noticeDismissTimers.delete(n.id);
      }
    }
  }
  orderStatusNotifications.value = nextV;
  orderStatusNotificationQueue.value = nextQ;
  schedulePromoteFromQueue();
};

watch(
  () => route.path,
  () => {
    pruneOrderNotificationsAlreadyShownElsewhere();
  }
);

/**
 * 显示通知
 */
const handleNoticeShow = infos => {
  const dedupeActive = shouldDedupeWalletEmbeddedOrderNotice();
  const dedupeKey = dedupeActive ? buildWalletEmbeddedNoticeDedupeKey(infos) : '';

  if (dedupeActive) {
    pruneOrderNotificationsAlreadyShownElsewhere();
    if (dedupeKey && readWalletEmbeddedShownNoticeKeySet().has(dedupeKey)) {
      return;
    }
  }

  // 内容
  let contentStr = infos.content;

  const noticeTimeTs = infos.sendTime != null && String(infos.sendTime) !== '' && Number.isFinite(Number(infos.sendTime)) ? Number(infos.sendTime) : Date.now();

  // 如果有日期，替换日期（风控 type=riskfail 不依赖接口 date，由组件/下方展示当前时间）
  if (infos.type !== 'riskfail' && infos.date) {
    const date = dayjs(infos.date).format('M月D日HH:mm');
    contentStr = contentStr.replace('[date]', date);
  } else if (infos.type === 'riskfail' && contentStr && contentStr.includes('[date]')) {
    contentStr = contentStr.replace('[date]', dayjs().format('M月D日HH:mm'));
  }

  const id = ++orderNoticeIdSeq;
  let built;
  if (infos.type === 'riskfail') {
    built = {
      title: infos.title,
      content: contentStr,
      type: infos.type,
      orderNo: infos.orderNo,
      mchOrderNo: infos.mchOrderNo,
      payCode: infos.payCode,
      displayTime: dayjs(noticeTimeTs).format('YYYY-MM-DD HH:mm'),
    };
  } else {
    built = {
      title: infos.title,
      content: contentStr,
      type: infos.type,
      ...walletOrderStatusPayload(infos),
    };
  }

  const manualDismissOnly = infos.type === 'riskfail' || (infos.type !== 'success' && infos.type !== 'partialsuccess');

  const entry = { id, infos: built, dedupeKey: dedupeKey || undefined, manualDismissOnly };

  if (dedupeActive && dedupeKey) {
    appendWalletEmbeddedShownNoticeKey(dedupeKey);
  }

  if (orderStatusNotifications.value.length < ORDER_NOTICE_VISIBLE_MAX) {
    orderStatusNotifications.value = [...orderStatusNotifications.value, entry];
    if (!manualDismissOnly) scheduleNoticeAutoDismiss(id);
  } else {
    orderStatusNotificationQueue.value = [...orderStatusNotificationQueue.value, entry];
  }

  // 刷新余额
  runEvent('balanceRefresh');
};

/**
 * 轮询订单信息
 */
const handlePollingOrderInfo = async isOnce => {
  const { accessToken } = counterInfoGet();

  if (!accessToken) {
    setTimeout(() => {
      handlePollingOrderInfo(isOnce);
    }, 500);
    return;
  }

  // 如果定时器存在 且 不是一次性轮询，则清除
  if (pollingOrderInfoTimer.value && !isOnce) {
    clearTimeout(pollingOrderInfoTimer.value);
  }

  try {
    // 获取订单信息
    const res = await getProcessStatic();

    // 如果返回值存在，则设置订单总数和是否跳转取款
    if (res) {
      // 写入存款/取款订单总数
      orderCountRC({
        depositCount: res.depositCount,
        processingPOrder: res.processingPOrder,
        receivingPayCount: res.receivingPayCount,
        pendingPayCount: res.pendingPayCount,
      });

      // 如果回调函数存在，则调用回调函数
      runEvent('orderChange', {
        depositCount: res.depositCount,
        processingPOrder: res.processingPOrder,
        receivingPayCount: res.receivingPayCount,
        pendingPayCount: res.pendingPayCount,
      });
    }

    // 如果是一次性轮询，则设置轮询编号
    if (isOnce) {
      return;
    }

    // 设置定时器
    pollingOrderInfoTimer.value = setTimeout(() => {
      // 如果回调函数存在，则调用回调函数
      handlePollingOrderInfo();
    }, 10000);
  } catch {
    // 如果是一次性轮询，则设置轮询编号
    if (isOnce) {
      return;
    }

    // 设置定时器
    pollingOrderInfoTimer.value = setTimeout(() => {
      // 如果回调函数存在，则调用回调函数
      handlePollingOrderInfo();
    }, 10000);
  }
};
</script>

<style lang="less" scoped>
.order-status-notifications-stack {
  position: fixed;
  top: 0.45rem;
  left: 0.08rem;
  right: 0.08rem;
  z-index: 3000;
  display: flex;
  flex-direction: column;
  pointer-events: none;

  &.walletShell {
    top: 0.02rem;
  }

  > * + * {
    margin-top: 0.08rem;
  }

  > * {
    pointer-events: auto;
  }

  &.fullScreen {
    margin-top: 0.5rem;
  }
}
</style>
